import React, { useState, useEffect } from 'react'
import { Card, Descriptions, Space, Button, Spin } from 'antd'
import { PhoneOutlined, MailOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import Agilite from 'agilite'
import BillingEnums from '../../../../utils/enums'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

const ContactDetailsContent = ({ accountData }) => {
  const [patientData, setPatientData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPatientData = async () => {
      // Validate patientId before making API call
      const patientId = accountData?.patientId
      if (!patientId || typeof patientId !== 'string' || patientId.trim() === '' || patientId.length < 10) {
        console.warn('❌ ContactDetails - Invalid patientId, skipping patient data fetch:', {
          patientId: patientId,
          type: typeof patientId,
          length: patientId?.length
        })
        setLoading(false)
        setPatientData(null) // Explicitly set to null so we only show account data
        return
      }

      try {
        // Use projection to fetch only the contact fields we need
        const response = await agilite.Connectors.execute('auth_users', BillingEnums.routeKeys.FIND_BY_ID, {
          recordId: accountData.patientId,
          projection: JSON.stringify({
            email: 1,
            phoneNumber: 1,
            residentialAddress: 1,
            firstName: 1,
            lastName: 1,
            _id: 1
          })
        })

        console.log('🔍 Contact Details - Patient data fetched:', response.data)
        setPatientData(response.data)
      } catch (error) {
        console.error('❌ ContactDetails - Failed to fetch patient data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchPatientData()
  }, [accountData?.patientId])

  if (loading) {
    return <Spin size='large' />
  }

  // Combine account data and patient data
  const combinedData = {
    ...accountData,
    ...patientData
  }

  return (
    <div>
      <Card size='small'>
        <Descriptions title='Contact Information' bordered column={2}>
          <Descriptions.Item label='Patient Name' span={2}>
            {combinedData.firstName && combinedData.lastName
              ? `${combinedData.firstName} ${combinedData.lastName}`
              : combinedData.patientName || '-'}
          </Descriptions.Item>

          {!patientData ? (
            <Descriptions.Item label='Contact Details' span={2}>
              <div style={{ color: '#999', fontStyle: 'italic' }}>
                Patient contact details not available. Patient ID: {accountData?.patientId || 'Not provided'}
              </div>
            </Descriptions.Item>
          ) : (
            <>
              <Descriptions.Item label='Mobile Number' span={1}>
                {combinedData.phoneNumber || '-'}
                {combinedData.phoneNumber && (
                  <Space style={{ marginLeft: 16 }}>
                    <Button
                      size='small'
                      icon={<PhoneOutlined />}
                      onClick={() => window.open(`tel:${combinedData.phoneNumber}`)}
                    >
                      Call
                    </Button>
                    <Button
                      size='small'
                      icon={<FontAwesomeIcon icon={faWhatsapp} />}
                      onClick={() => window.open(`https://wa.me/${combinedData.phoneNumber.replace(/\D/g, '')}`)}
                    >
                      WhatsApp
                    </Button>
                  </Space>
                )}
              </Descriptions.Item>
              <Descriptions.Item label='Email' span={1}>
                {combinedData.email || '-'}
                {combinedData.email && (
                  <Button
                    size='small'
                    icon={<MailOutlined />}
                    style={{ marginLeft: 16 }}
                    onClick={() => window.open(`mailto:${combinedData.email}`)}
                  >
                    Send Email
                  </Button>
                )}
              </Descriptions.Item>
              <Descriptions.Item label='Residential Address' span={2}>
                {combinedData.residentialAddress || '-'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>
    </div>
  )
}

export default ContactDetailsContent
